import { Suspense, useCallback, useEffect, useRef, type MouseEvent, type RefObject } from 'react'
import { useIntl } from 'react-intl'
import { Outlet, useLocation, useNavigationType } from 'react-router'

import { shellSlots, SlotPanels } from '@/shared/registry'

import { AppFooter } from './AppFooter'
import { AppHeader } from './AppHeader'
import { isRedirect } from './Redirect'
import { PageSkeleton } from './Skeleton'

const mainId = 'main-content'

/** Fills the page region while a route's chunk is fetched. */
export function PageFallback() {
  const intl = useIntl()

  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 py-8 sm:px-6 lg:py-10">
      <PageSkeleton label={intl.formatMessage({ id: 'shell.loadingPage' })} />
    </div>
  )
}

/**
 * Header, page, footer: the frame every route renders in. A skip link comes first; `Suspense`
 * sits inside `main` so a lazy route load does not blank the header and footer. The root clips
 * horizontal overflow, so a full-bleed page header band never adds a scroll bar.
 */
export function AppShell() {
  const shellRef = useRef<HTMLDivElement>(null)
  const mainRef = useRef<HTMLElement>(null)
  useScrollToTopOnNavigation()
  useFocusHeadingOnNavigation(shellRef, mainRef)

  return (
    <div ref={shellRef} className="flex min-h-screen flex-col overflow-x-clip bg-background">
      <SkipLink />
      <AppHeader />
      <main ref={mainRef} id={mainId} tabIndex={-1} className="flex flex-1 flex-col outline-none">
        <Suspense fallback={<PageFallback />}>
          <Outlet />
        </Suspense>
      </main>
      <AppFooter />
      {/* Hidden, so a gate's loading or failure never shows; its modal renders outside the box. */}
      <SlotPanels slot={shellSlots.gates} className="hidden" />
    </div>
  )
}

/**
 * Hidden until focused; moves focus past the header to the page. Shown, it takes a row of its own
 * above the header, padded to well over 24 px, rather than covering the logo link.
 */
function SkipLink() {
  const intl = useIntl()

  const skip = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    // Focus without changing the address, which the router would otherwise take as navigation.
    event.preventDefault()
    document.getElementById(mainId)?.focus()
  }, [])

  return (
    <a
      href={`#${mainId}`}
      onClick={skip}
      className="sr-only self-start rounded bg-primary-main text-sm font-semibold text-primary-contrast focus:not-sr-only focus:m-2 focus:px-4 focus:py-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-main"
    >
      {intl.formatMessage({ id: 'shell.skipToContent' })}
    </a>
  )
}

/**
 * Every page opens at its top, as the mockups' App does, rather than where the browser kept the
 * scroll, such as mid-list after Back. A change within the page, such as a search's address, keeps it.
 */
function useScrollToTopOnNavigation() {
  const { pathname } = useLocation()
  // None at first, so the first page also opens at its top, such as after a reload.
  const shown = useRef<string | null>(null)
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
  }, [])
  useEffect(() => {
    if (shown.current !== pathname) {
      shown.current = pathname
      window.scrollTo(0, 0)
    }
  }, [pathname])
}

/** Whether focus was lost, as when the link that was used closed with its menu. */
function focusIsLost(): boolean {
  const active = document.activeElement
  return active === null || active === document.body || !active.isConnected
}

/** Whether a modal dialog, such as the shared `Dialog`, is open over the page. */
function modalIsOpen(): boolean {
  return (
    document.querySelector('[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]') !== null
  )
}

/** Whether an open modal dialog holds focus; a closing one has already given it back to the page. */
function dialogHoldsFocus(shell: HTMLElement): boolean {
  const active = document.activeElement
  return modalIsOpen() && (active === null || !shell.contains(active))
}

/**
 * After every navigation but the first load, moves focus to the new page's `h1`, so keyboard and
 * screen reader users start at the page they asked for. A navigation that stays on the same page,
 * such as a section chosen on Admin Home or the page's own link chosen again, keeps focus where
 * it is unless focus was lost. A lazy page's heading appears later, so it is watched for until
 * it does. A page may then replace that heading, as a loading header does with Not Found, so
 * until the next navigation focus follows to the new `h1`, unless the user has moved it.
 *
 * Focus never moves behind an open modal dialog. Once the dialog is gone, focus stays where it
 * returned it, or goes to the `h1` if that control is gone, as after a navigation.
 */
function useFocusHeadingOnNavigation(
  shellRef: RefObject<HTMLElement | null>,
  mainRef: RefObject<HTMLElement | null>,
) {
  const { pathname, key, state } = useLocation()
  // Only the redirect itself: Back to an entry it left keeps its state, but is a POP.
  const redirected = useNavigationType() === 'REPLACE' && isRedirect(state)
  const previous = useRef<{ readonly pathname: string; readonly key: string; readonly isFirstLoad: boolean } | null>(null)

  useEffect(() => {
    const shell = shellRef.current
    const main = mainRef.current
    const last = previous.current
    // The first load keeps focus where the browser puts it, as does a redirect made in its place.
    const isFirstLoad = last === null || (last.isFirstLoad && (last.key === key || redirected))
    previous.current = { pathname, key, isFirstLoad }
    if (shell === null || main === null || last === null || isFirstLoad || last.key === key) {
      return undefined
    }
    // A dialog may close onto a control the earlier navigation removed, so it is still watched.
    if (last.pathname === pathname && !focusIsLost() && !dialogHoldsFocus(shell)) {
      return undefined
    }

    let focused: HTMLElement | null = null
    // An open dialog held focus when the heading would have taken it.
    let heldBack = false
    const focusHeading = () => {
      if (dialogHoldsFocus(shell)) {
        heldBack = true
        return
      }
      if (heldBack) {
        // Once the dialog is gone, focus stays where it gave it back, unless that is gone too.
        if (modalIsOpen()) {
          return
        }
        if (!focusIsLost()) {
          stop()
          return
        }
      }
      const heading = main.querySelector('h1')
      if (heading === null) {
        return
      }
      if (!heading.hasAttribute('tabindex')) {
        heading.setAttribute('tabindex', '-1')
      }
      focused = heading
      // The scroll is the shell's to set (useScrollToTopOnNavigation), so a change only to the address keeps it.
      heading.focus({ preventScroll: true })
    }

    const observer = new MutationObserver(() => {
      if (focused === null || (!focused.isConnected && focusIsLost())) {
        focusHeading()
      }
    })
    // Focus moved to anything but the heading, or off it while it is still shown, is the user's.
    const onFocusIn = (event: FocusEvent) => {
      if (focused !== null && event.target !== focused) {
        stop()
      }
    }
    const onFocusOut = (event: FocusEvent) => {
      const heading = focused
      if (heading !== null && event.target === heading) {
        // Some browsers blur a heading being removed before it is gone, so look once that settles.
        queueMicrotask(() => {
          if (heading.isConnected) {
            stop()
          }
        })
      }
    }
    function stop() {
      observer.disconnect()
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
    }

    focusHeading()
    observer.observe(main, { childList: true, subtree: true })
    // A closed dialog leaves the body, taking focus with it if what opened it is gone.
    observer.observe(document.body, { childList: true })
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    return stop
  }, [shellRef, mainRef, pathname, key, redirected])
}
